import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "@/lib/toast";
import { MobileFrame } from "@/components/MobileFrame";
import { Icon } from "@/components/primitives";
import { PricingTelemetryPanel } from "@/components/admin/PricingTelemetryPanel";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/hooks/useAuth";
import { useAdminRole } from "@/hooks/useAdminRole";

// One-stop admin hub for everything human-in-the-loop:
//   • Disagreements    — review/curate verdict_feedback rows where users said Fig was off
//   • Suggestions      — read user-submitted "make Fig better" notes from feedback_suggestions
//   • Lessons          — view the distilled correction_lessons that get injected into the AI prompt,
//                        and re-run the distillation on demand
// All gated behind the existing `admin` role via private.has_role.

type FeedbackRow = {
  id: string;
  scan_id: string;
  user_id: string;
  reason: string | null;
  notes: string | null;
  excluded: boolean;
  reviewed_at: string | null;
  created_at: string;
  scans: {
    id: string;
    title: string | null;
    brand: string | null;
    category: string | null;
    verdict: string | null;
    verdict_label: string | null;
    photos: string[] | null;
  } | null;
};

type Suggestion = {
  id: string;
  user_id: string;
  category: string;
  body: string;
  created_at: string;
  status: "new" | "approved" | "archived" | "spam";
};

type Lesson = {
  id: string;
  scope_kind: string;
  scope_label: string;
  lessons: string;
  sample_count: number;
  updated_at: string;
};

type WaitlistEntry = {
  id: string;
  email: string;
  first_name: string;
  approved: boolean;
  invited_at: string | null;
  created_at: string;
};

type SuspendedScan = {
  id: string;
  user_id: string;
  brand: string | null;
  title: string | null;
  era: string | null;
  category: string | null;
  suspended_at: string | null;
  suspension_reason: string | null;
  processing_status: string | null;
  posted: boolean | null;
};

type ModeratorApplication = {
  id: string;
  user_id: string;
  years_collecting: string | null;
  specialty: string | null;
  closet_pieces: string | null;
  motivation: string | null;
  status: string;
  reviewer_notes: string | null;
  created_at: string;
  // joined profile handle (fetched separately)
  handle?: string | null;
};

type Tab = "disagreements" | "suggestions" | "lessons" | "waitlist" | "suspensions" | "applications" | "invites" | "tele";

export default function Admin() {
  const nav = useNavigate();
  const { user } = useAuth();
  const { isAdmin, adminLoading } = useAdminRole();
  const [tab, setTab] = useState<Tab>("waitlist");

  // Disagreements state
  const [feedback, setFeedback] = useState<FeedbackRow[]>([]);
  const [feedbackFilter, setFeedbackFilter] = useState<"open" | "all">("open");
  const [feedbackLoading, setFeedbackLoading] = useState(false);

  // Suggestions
  const [suggestions, setSuggestions] = useState<Suggestion[]>([]);
  const [sugLoading, setSugLoading] = useState(false);
  const [sugFilter, setSugFilter] = useState<"new" | "approved" | "archived" | "spam" | "all">("new");
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [bulkBusy, setBulkBusy] = useState(false);

  // Lessons
  const [lessons, setLessons] = useState<Lesson[]>([]);
  const [lessonsLoading, setLessonsLoading] = useState(false);
  const [running, setRunning] = useState(false);

  // Waitlist
  const [waitlist, setWaitlist] = useState<WaitlistEntry[]>([]);
  const [waitlistLoading, setWaitlistLoading] = useState(false);
  const [waitlistFilter, setWaitlistFilter] = useState<"pending" | "all">("pending");
  const [inviting, setInviting] = useState<Set<string>>(new Set());

  // Suspensions
  const [suspended, setSuspended] = useState<SuspendedScan[]>([]);
  const [suspendedLoading, setSuspendedLoading] = useState(false);
  const [reportCounts, setReportCounts] = useState<Record<string, number>>({});

  // Applications
  const [applications, setApplications] = useState<ModeratorApplication[]>([]);
  const [applicationsLoading, setApplicationsLoading] = useState(false);
  // Direct-invite flow state (separate from the applications flow above).
  const [inviteHandleInput, setInviteHandleInput] = useState("");
  const [inviteMessage, setInviteMessage] = useState("");
  const [inviteCandidate, setInviteCandidate] = useState<{ id: string; handle: string; display_name: string | null } | null>(null);
  const [inviteLookupErr, setInviteLookupErr] = useState<string | null>(null);
  const [inviteBusy, setInviteBusy] = useState(false);
  const [expandedApp, setExpandedApp] = useState<Set<string>>(new Set());

  // ─── Loaders ──────────────────────────────────────────────────
  const loadFeedback = async () => {
    setFeedbackLoading(true);
    let q = supabase
      .from("verdict_feedback")
      .select("id, scan_id, user_id, reason, notes, excluded, reviewed_at, created_at")
      .eq("agree", false)
      .order("created_at", { ascending: false })
      .limit(100);
    if (feedbackFilter === "open") q = q.is("reviewed_at", null).eq("excluded", false);
    const { data, error } = await q;
    setFeedbackLoading(false);
    if (error) { console.error("[admin] loadFeedback failed", error); return toast.error(`Feedback: ${error.message}`); }
    // Attach scan data separately to avoid join permission issues on scans table.
    const rows = (data ?? []) as Omit<FeedbackRow, "scans">[];
    const scanIds = [...new Set(rows.map((r) => r.scan_id))];
    let scanMap: Record<string, FeedbackRow["scans"]> = {};
    if (scanIds.length > 0) {
      const { data: scansData } = await supabase
        .from("scans")
        .select("id, title, brand, category, verdict, verdict_label, photos")
        .in("id", scanIds);
      for (const s of scansData ?? []) scanMap[s.id] = s as FeedbackRow["scans"];
    }
    setFeedback(rows.map((r) => ({ ...r, scans: scanMap[r.scan_id] ?? null })) as FeedbackRow[]);
  };

  const loadSuggestions = async () => {
    setSugLoading(true);
    let q = supabase
      .from("feedback_suggestions")
      .select("id, user_id, category, body, created_at, status")
      .order("created_at", { ascending: false })
      .limit(200);
    if (sugFilter !== "all") q = q.eq("status", sugFilter);
    const { data, error } = await q;
    setSugLoading(false);
    if (error) return toast.error("Couldn't load suggestions");
    setSuggestions((data ?? []) as Suggestion[]);
    setSelected(new Set());
  };

  const loadWaitlist = async () => {
    setWaitlistLoading(true);
    setWaitlist([]);
    let q = supabase
      .from("waitlist")
      .select("id, email, first_name, approved, invited_at, created_at")
      .order("created_at", { ascending: false })
      .limit(500);
    // Match both approved=false and approved=null (entries before the column was added)
    if (waitlistFilter === "pending") q = (q as any).or("approved.eq.false,approved.is.null");
    const { data, error } = await q;
    setWaitlistLoading(false);
    if (error) { toast.error("Couldn't load waitlist"); return; }
    setWaitlist((data ?? []) as WaitlistEntry[]);
  };

  const loadLessons = async () => {
    setLessonsLoading(true);
    const { data, error } = await supabase
      .from("correction_lessons")
      .select("id, scope_kind, scope_label, lessons, sample_count, updated_at")
      .order("updated_at", { ascending: false })
      .limit(200);
    setLessonsLoading(false);
    if (error) return toast.error("Couldn't load lessons");
    setLessons((data ?? []) as Lesson[]);
  };

  const loadSuspensions = async () => {
    setSuspendedLoading(true);
    const { data, error } = await supabase
      .from("scans")
      .select("id, user_id, brand, title, era, category, suspended_at, suspension_reason, processing_status, posted")
      .not("suspended_at", "is", null)
      .order("suspended_at", { ascending: false })
      .limit(100);
    setSuspendedLoading(false);
    if (error) { toast.error(`Suspensions: ${error.message}`); return; }
    const rows = (data ?? []) as SuspendedScan[];
    setSuspended(rows);
    // Fetch report counts
    const scanIds = rows.map((r) => r.id);
    if (scanIds.length > 0) {
      const { data: reports } = await supabase
        .from("fast_fashion_reports")
        .select("scan_id")
        .in("scan_id", scanIds);
      const counts: Record<string, number> = {};
      for (const r of reports ?? []) {
        counts[r.scan_id] = (counts[r.scan_id] ?? 0) + 1;
      }
      setReportCounts(counts);
    } else {
      setReportCounts({});
    }
  };

  const loadApplications = async () => {
    setApplicationsLoading(true);
    const { data, error } = await supabase
      .from("moderator_applications")
      .select("id, user_id, years_collecting, specialty, closet_pieces, motivation, status, reviewer_notes, created_at")
      .order("created_at", { ascending: false })
      .limit(100);
    setApplicationsLoading(false);
    if (error) { toast.error(`Applications: ${error.message}`); return; }
    const rows = (data ?? []) as ModeratorApplication[];
    // Fetch handles from profiles
    const userIds = [...new Set(rows.map((r) => r.user_id))];
    let handleMap: Record<string, string | null> = {};
    if (userIds.length > 0) {
      const { data: profiles } = await supabase
        .from("profiles")
        .select("id, handle")
        .in("id", userIds);
      for (const p of profiles ?? []) handleMap[p.id] = (p as any).handle ?? null;
    }
    setApplications(rows.map((r) => ({ ...r, handle: handleMap[r.user_id] ?? null })));
  };

  useEffect(() => {
    if (!isAdmin) return;
    if (tab === "disagreements") loadFeedback();
    if (tab === "suggestions") loadSuggestions();
    if (tab === "lessons") loadLessons();
    if (tab === "waitlist") loadWaitlist();
    if (tab === "suspensions") loadSuspensions();
    if (tab === "applications") loadApplications();
    // eslint-disable-next-line
  }, [isAdmin, tab, feedbackFilter, sugFilter, waitlistFilter]);

  // Pending counts for tab badges — refreshed on mount so the tab strip
  // surfaces a number even before the admin clicks into the tab. Lightweight
  // count() queries; no full row fetch.
  const [pendingApplications, setPendingApplications] = useState<number>(0);
  const [pendingSuspensions, setPendingSuspensions] = useState<number>(0);
  useEffect(() => {
    if (!isAdmin) return;
    (async () => {
      const { count: appCount } = await supabase
        .from("moderator_applications")
        .select("id", { count: "exact", head: true })
        .eq("status", "pending");
      setPendingApplications(appCount ?? 0);
      const { count: suspCount } = await supabase
        .from("scans")
        .select("id", { count: "exact", head: true })
        .not("suspended_at", "is", null);
      setPendingSuspensions(suspCount ?? 0);
    })();
  }, [isAdmin, tab]);

  // Auto-refresh waitlist every 30 s while the tab is open
  useEffect(() => {
    if (!isAdmin || tab !== "waitlist") return;
    const interval = setInterval(() => loadWaitlist(), 30_000);
    return () => clearInterval(interval);
    // eslint-disable-next-line
  }, [isAdmin, tab, waitlistFilter]);

  // ─── Actions ──────────────────────────────────────────────────
  const markReviewed = async (id: string) => {
    if (!user) return;
    const { error } = await supabase.from("verdict_feedback")
      .update({ reviewed_at: new Date().toISOString(), reviewed_by: user.id })
      .eq("id", id);
    if (error) return toast.error("Couldn't mark reviewed");
    if (feedbackFilter === "open") setFeedback((rs) => rs.filter((r) => r.id !== id));
    else setFeedback((rs) => rs.map((r) => r.id === id ? { ...r, reviewed_at: new Date().toISOString() } : r));
    toast.success("Marked reviewed");
  };

  const toggleExcluded = async (id: string, next: boolean) => {
    if (!user) return;
    const { error } = await supabase.from("verdict_feedback")
      .update({ excluded: next, reviewed_at: new Date().toISOString(), reviewed_by: user.id })
      .eq("id", id);
    if (error) return toast.error("Couldn't update");
    setFeedback((rs) => rs.map((r) => r.id === id ? { ...r, excluded: next, reviewed_at: new Date().toISOString() } : r));
    toast.success(next ? "Hidden from training" : "Re-included");
  };

  const approveAndInvite = async (entry: WaitlistEntry) => {
    setInviting((prev) => new Set(prev).add(entry.id));
    const { data, error } = await supabase.functions.invoke("approve-waitlist", {
      body: { email: entry.email },
    });
    setInviting((prev) => { const next = new Set(prev); next.delete(entry.id); return next; });
    if (error || data?.error) {
      toast.error(data?.error ?? "Invite failed");
      return;
    }
    if (data?.already) { toast.success("Already invited"); return; }
    toast.success(`Invite sent to ${entry.email}`);
    setWaitlist((rows) => rows.map((r) =>
      r.id === entry.id ? { ...r, approved: true, invited_at: new Date().toISOString() } : r
    ));
    if (waitlistFilter === "pending") {
      setWaitlist((rows) => rows.filter((r) => r.id !== entry.id));
    }
  };

  const unapprove = async (entry: WaitlistEntry) => {
    setInviting((prev) => new Set(prev).add(entry.id));
    const { error } = await supabase
      .from("waitlist")
      .update({ approved: false, invited_at: null } as never)
      .eq("id", entry.id);
    setInviting((prev) => { const next = new Set(prev); next.delete(entry.id); return next; });
    if (error) { toast.error("Couldn't unapprove"); return; }
    toast.success(`${entry.email} unapproved`);
    setWaitlist((rows) => rows.map((r) =>
      r.id === entry.id ? { ...r, approved: false, invited_at: null } : r
    ));
  };

  const runDistill = async () => {
    setRunning(true);
    const { error } = await supabase.functions.invoke("distill-corrections", { body: {} });
    setRunning(false);
    if (error) return toast.error("Distill failed");
    toast.success("Distillation complete");
    loadLessons();
  };

  // ─── Suspension actions ───────────────────────────────────────
  const confirmSuspension = async (scan: SuspendedScan) => {
    await supabase.from("notifications").insert({
      user_id: scan.user_id,
      type: "post_removed",
      scan_id: scan.id,
      actor_id: null,
      read: false,
    });
    await loadSuspensions();
    toast.success("Suspension confirmed");
  };

  const restorePost = async (scan: SuspendedScan) => {
    const { error } = await supabase
      .from("scans")
      .update({ suspended_at: null, suspension_reason: null })
      .eq("id", scan.id);
    if (error) { toast.error(error.message); return; }
    await supabase.from("notifications").insert({
      user_id: scan.user_id,
      type: "post_reinstated",
      scan_id: scan.id,
      actor_id: null,
      read: false,
    });
    await loadSuspensions();
    toast.success("Post restored");
  };

  // ─── Application actions ──────────────────────────────────────
  const approveApplication = async (app: ModeratorApplication) => {
    const { error: appErr } = await supabase
      .from("moderator_applications")
      .update({
        status: "approved",
        reviewed_by: user?.id ?? null,
        reviewed_at: new Date().toISOString(),
      })
      .eq("id", app.id);
    if (appErr) { toast.error(appErr.message); return; }

    const { error: profErr } = await supabase
      .from("profiles")
      .update({ is_verified_moderator: true } as never)
      .eq("id", app.user_id);
    if (profErr) { toast.error(profErr.message); return; }

    toast.success("Application approved");
    await loadApplications();
  };

  // ─── Direct moderator invites (no application required) ──────────────
  const lookupInviteCandidate = async () => {
    const raw = inviteHandleInput.trim().replace(/^@/, "").toLowerCase();
    setInviteLookupErr(null);
    setInviteCandidate(null);
    if (!raw) {
      setInviteLookupErr("Enter a handle to look up.");
      return;
    }
    const { data, error } = await (supabase as never as {
      from: (t: string) => {
        select: (cols: string) => {
          eq: (col: string, val: string) => {
            maybeSingle: () => Promise<{ data: { id: string; handle: string; display_name: string | null } | null; error: { message: string } | null }>;
          };
        };
      };
    })
      .from("profiles_public")
      .select("id, handle, display_name")
      .eq("handle", raw)
      .maybeSingle();
    if (error) { setInviteLookupErr(error.message); return; }
    if (!data) { setInviteLookupErr(`No collector with handle @${raw}.`); return; }
    setInviteCandidate(data);
  };

  const sendModeratorInvite = async () => {
    if (!inviteCandidate || !user) return;
    setInviteBusy(true);
    // Insert the invite row. RLS enforces admin-only.
    const { data: inviteRow, error: invErr } = await (supabase as never as {
      from: (t: string) => {
        insert: (rows: Record<string, unknown>) => {
          select: () => {
            single: () => Promise<{ data: { id: string } | null; error: { message: string } | null }>;
          };
        };
      };
    })
      .from("moderator_invites")
      .insert({
        user_id: inviteCandidate.id,
        invited_by: user.id,
        message: inviteMessage.trim() || null,
        status: "pending",
      })
      .select()
      .single();

    if (invErr || !inviteRow) {
      setInviteBusy(false);
      // 23505 unique violation → "they already have a pending invite"
      if (invErr?.message && /duplicate|unique/i.test(invErr.message)) {
        toast.error(`@${inviteCandidate.handle} already has a pending invite.`);
      } else {
        toast.error(invErr?.message ?? "Couldn't send invite.");
      }
      return;
    }

    // Then a notification pointing at the new invite page.
    const { error: notErr } = await (supabase as never as {
      from: (t: string) => {
        insert: (rows: Record<string, unknown>) => Promise<{ error: { message: string } | null }>;
      };
    })
      .from("notifications")
      .insert({
        user_id: inviteCandidate.id,
        actor_id: user.id,
        type: "moderator_invite",
        title: "You've been invited to be a verified collector",
        body: "Tap to see what it means and accept or decline.",
        data: { invite_id: inviteRow.id },
        read: false,
      });
    setInviteBusy(false);
    if (notErr) {
      toast.error(`Invite created but notification failed: ${notErr.message}`);
      return;
    }
    toast.success(`Invite sent to @${inviteCandidate.handle}.`);
    setInviteHandleInput("");
    setInviteMessage("");
    setInviteCandidate(null);
  };

  const rejectApplication = async (app: ModeratorApplication) => {
    const notes = window.prompt("Reviewer notes (optional):");
    // null means the user cancelled the prompt — treat as a deliberate skip (empty notes)
    const { error } = await supabase
      .from("moderator_applications")
      .update({
        status: "rejected",
        reviewer_notes: notes ?? null,
        reviewed_by: user?.id ?? null,
        reviewed_at: new Date().toISOString(),
      })
      .eq("id", app.id);
    if (error) { toast.error(error.message); return; }
    toast.success("Application rejected");
    await loadApplications();
  };

  const toggleExpandedApp = (id: string) => {
    setExpandedApp((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  // ─── Suggestion bulk actions ──────────────────────────────────
  const toggleSel = (id: string) => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };
  const toggleAll = () => {
    if (selected.size === suggestions.length) setSelected(new Set());
    else setSelected(new Set(suggestions.map((s) => s.id)));
  };
  const bulkSetStatus = async (status: "approved" | "archived" | "spam" | "new") => {
    if (!user || selected.size === 0) return;
    setBulkBusy(true);
    const ids = Array.from(selected);
    const { error } = await supabase.from("feedback_suggestions")
      .update({ status, reviewed_at: new Date().toISOString(), reviewed_by: user.id })
      .in("id", ids);
    setBulkBusy(false);
    if (error) return toast.error("Bulk update failed");
    toast.success(`${ids.length} marked ${status}`);
    if (sugFilter !== "all") {
      setSuggestions((rs) => rs.filter((r) => !selected.has(r.id)));
    } else {
      setSuggestions((rs) => rs.map((r) => selected.has(r.id) ? { ...r, status } : r));
    }
    setSelected(new Set());
  };

  // ─── Render ───────────────────────────────────────────────────
  if (adminLoading) {
    return <MobileFrame><div className="paper-bg" style={{ minHeight: "100vh", padding: 80, color: "hsl(var(--ink-2))" }}>Checking access…</div></MobileFrame>;
  }
  if (isAdmin === false) {
    return (
      <MobileFrame>
        <div className="paper-bg" style={{ minHeight: "100vh", padding: 80 }}>
          <h1 style={{ fontFamily: "'Fraunces', 'Playfair Display', serif", fontSize: 28, color: "hsl(var(--ink))" }}>Not authorized</h1>
          <p style={{ marginTop: 8, color: "hsl(var(--ink-2))" }}>This page is for admins only.</p>
          <button onClick={() => nav("/home")} className="btn" style={{ marginTop: 16, padding: "10px 14px" }}>Go home</button>
        </div>
      </MobileFrame>
    );
  }

  return (
    <MobileFrame>
      <div className="paper-bg" style={{ minHeight: "100vh", padding: "16px 16px 40px" }}>
        {/* ─── Header: back chevron + page title ─────────────────────────── */}
        <div style={{
          display: "flex", alignItems: "center", gap: 8,
          marginBottom: 14,
        }}>
          <button
            onClick={() => nav(-1)}
            aria-label="Back"
            style={{
              background: "none", border: "none", padding: 6, cursor: "pointer",
              color: "hsl(var(--ink))", marginLeft: -6,
            }}
          >{Icon.chev(18, "l")}</button>
          <div style={{ flex: 1, minWidth: 0 }}>
            <h1 style={{
              fontFamily: "'Fraunces', 'Playfair Display', serif", fontWeight: 800, fontSize: 26,
              color: "hsl(var(--ink))", lineHeight: 1.05, letterSpacing: "-0.02em",
              margin: 0,
            }}>Admin</h1>
            <div style={{
              fontFamily: "'Fraunces', 'Playfair Display', serif", fontSize: 12,
              color: "hsl(var(--ink-2))", marginTop: 2,
            }}>
              Human-in-the-loop · correct Fig, invite collectors, moderate posts.
            </div>
          </div>
        </div>

        {/* ─── Tab strip ──────────────────────────────────────────────────
            Horizontal scroll keeps the 8 tabs on a single row with full
            labels instead of jagged wrap rows. Scrollbar chrome is hidden
            but touch-swipe still works. Pending counts surface as rust
            pills so workload is visible at a glance. */}
        <style>{`.admin-tabs::-webkit-scrollbar { display: none; }`}</style>
        <div className="admin-tabs" style={{
          display: "flex", gap: 6, marginBottom: 16,
          borderBottom: "1px solid hsl(var(--ink))",
          overflowX: "auto", overflowY: "hidden",
          scrollbarWidth: "none", msOverflowStyle: "none",
        } as React.CSSProperties}>
          {([
            ["waitlist",      "Waitlist",      null],
            ["applications",  "Applications",  pendingApplications],
            ["invites",       "Invite Mods",   null],
            ["suspensions",   "Suspensions",   pendingSuspensions],
            ["disagreements", "Disagreements", null],
            ["suggestions",   "Suggestions",   null],
            ["lessons",       "Lessons",       null],
            ["tele",          "Telemetry",     null],
          ] as const).map(([id, label, badge]) => (
            <button key={id} onClick={() => setTab(id)} style={{
              flex: "0 0 auto", padding: "10px 14px",
              background: tab === id ? "hsl(var(--ink))" : "transparent",
              color: tab === id ? "hsl(var(--ink-fg))" : "hsl(var(--ink))",
              border: "1px solid hsl(var(--ink))",
              borderBottom: tab === id ? "1px solid hsl(var(--ink))" : "1px solid transparent",
              marginBottom: -1,
              fontFamily: "'IBM Plex Mono', monospace", fontSize: 11,
              letterSpacing: "0.08em", textTransform: "uppercase", cursor: "pointer",
              whiteSpace: "nowrap",
              display: "inline-flex", alignItems: "center", justifyContent: "center", gap: 6,
            }}>
              <span>{label}</span>
              {badge != null && badge > 0 && (
                <span style={{
                  display: "inline-flex", alignItems: "center", justifyContent: "center",
                  minWidth: 18, height: 18, padding: "0 5px", borderRadius: 9,
                  background: "hsl(var(--rust))", color: "hsl(var(--rust-fg))",
                  fontSize: 9, fontWeight: 700, letterSpacing: 0,
                }}>{badge}</span>
              )}
            </button>
          ))}
        </div>

        {/* ─── Disagreements ─── */}
        {tab === "disagreements" && (
          <>
            <div style={{ display: "flex", gap: 6, marginBottom: 12 }}>
              {(["open", "all"] as const).map((f) => (
                <button key={f} onClick={() => setFeedbackFilter(f)} style={{
                  flex: 1, padding: "8px",
                  background: feedbackFilter === f ? "hsl(var(--ink))" : "hsl(var(--cream))",
                  color: feedbackFilter === f ? "hsl(var(--ink-fg))" : "hsl(var(--ink))",
                  border: "1px solid hsl(var(--ink))",
                  fontFamily: "'IBM Plex Mono', monospace", fontSize: 11,
                  letterSpacing: "0.08em", textTransform: "uppercase", cursor: "pointer",
                }}>{f === "open" ? "Open" : "All recent"}</button>
              ))}
            </div>
            {feedbackLoading && <div style={{ color: "hsl(var(--ink-2))", padding: 12 }}>Loading…</div>}
            {!feedbackLoading && feedback.length === 0 && (
              <div style={{ padding: 18, fontFamily: "'Fraunces', 'Playfair Display', serif", color: "hsl(var(--ink-2))" }}>
                Nothing to review. The hive is in agreement.
              </div>
            )}
            <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
              {feedback.map((r) => {
                const text = `${r.reason ?? ""} ${r.notes ?? ""}`.trim();
                const photo = r.scans?.photos?.[0];
                return (
                  <div key={r.id} style={{
                    border: "1px solid hsl(var(--ink))", padding: 12,
                    background: r.excluded ? "hsl(var(--paper-3))" : "hsl(var(--cream))",
                  }}>
                    <div style={{ display: "flex", gap: 10 }}>
                      {photo && <img src={photo} alt="" style={{ width: 56, height: 56, objectFit: "cover", border: "1px solid hsl(var(--ink))" }} />}
                      <div style={{ flex: 1, minWidth: 0 }}>
                        <div style={{ fontFamily: "'Fraunces', 'Playfair Display', serif", fontWeight: 700, fontSize: 14, color: "hsl(var(--ink))" }}>
                          {r.scans?.title ?? "Untitled scan"}
                        </div>
                        <div className="caps" style={{ color: "hsl(var(--ink-2))", marginTop: 2, fontSize: 10 }}>
                          {r.scans?.brand ?? "Unknown"} · {r.scans?.category ?? "—"} · Fig: {r.scans?.verdict_label ?? r.scans?.verdict ?? "—"}
                        </div>
                      </div>
                    </div>
                    {text && (
                      <div style={{
                        marginTop: 10, padding: 10,
                        background: "hsl(var(--paper-2))", border: "1px dashed hsl(var(--sand-deep))",
                        fontFamily: "'IBM Plex Mono', monospace", fontSize: 12, color: "hsl(var(--ink))",
                        whiteSpace: "pre-wrap",
                      }}>{text}</div>
                    )}
                    <div style={{ display: "flex", gap: 6, marginTop: 10, flexWrap: "wrap" }}>
                      <button onClick={() => markReviewed(r.id)} className="caps" style={{
                        flex: 1, padding: "8px", background: "hsl(var(--olive))", color: "hsl(var(--cream))",
                        border: "1px solid hsl(var(--ink))", cursor: "pointer",
                      }}>Mark reviewed</button>
                      <button onClick={() => toggleExcluded(r.id, !r.excluded)} className="caps" style={{
                        flex: 1, padding: "8px",
                        background: r.excluded ? "hsl(var(--cream))" : "hsl(var(--rust))",
                        color: r.excluded ? "hsl(var(--ink))" : "hsl(var(--cream))",
                        border: "1px solid hsl(var(--ink))", cursor: "pointer",
                      }}>{r.excluded ? "Re-include" : "Hide from training"}</button>
                    </div>
                    <div className="caps" style={{ marginTop: 6, color: "hsl(var(--ink-2))", fontSize: 10 }}>
                      {new Date(r.created_at).toLocaleString()} {r.reviewed_at && "· reviewed"} {r.excluded && "· excluded"}
                    </div>
                  </div>
                );
              })}
            </div>
          </>
        )}

        {/* ─── Suggestions ─── */}
        {tab === "suggestions" && (
          <>
            <div style={{ display: "flex", gap: 4, marginBottom: 10, flexWrap: "wrap" }}>
              {(["new", "approved", "archived", "spam", "all"] as const).map((f) => (
                <button key={f} onClick={() => setSugFilter(f)} style={{
                  flex: "1 1 auto", padding: "6px 8px",
                  background: sugFilter === f ? "hsl(var(--ink))" : "hsl(var(--cream))",
                  color: sugFilter === f ? "hsl(var(--ink-fg))" : "hsl(var(--ink))",
                  border: "1px solid hsl(var(--ink))",
                  fontFamily: "'IBM Plex Mono', monospace", fontSize: 10,
                  letterSpacing: "0.08em", textTransform: "uppercase", cursor: "pointer",
                }}>{f}</button>
              ))}
            </div>
            {suggestions.length > 0 && (
              <div style={{
                display: "flex", gap: 6, alignItems: "center", flexWrap: "wrap",
                padding: 8, marginBottom: 10,
                background: "hsl(var(--paper-2))", border: "1px solid hsl(var(--ink))",
              }}>
                <button onClick={toggleAll} className="caps" style={{
                  padding: "6px 10px", background: "hsl(var(--cream))",
                  color: "hsl(var(--ink))", border: "1px solid hsl(var(--ink))",
                  fontSize: 10, cursor: "pointer",
                }}>{selected.size === suggestions.length ? "Clear" : "Select all"}</button>
                <span className="caps" style={{ fontSize: 10, color: "hsl(var(--ink-2))" }}>
                  {selected.size} selected
                </span>
                <div style={{ display: "flex", gap: 4, marginLeft: "auto", flexWrap: "wrap" }}>
                  <button disabled={!selected.size || bulkBusy} onClick={() => bulkSetStatus("approved")} className="caps" style={{
                    padding: "6px 10px", background: "hsl(var(--olive))", color: "hsl(var(--cream))",
                    border: "1px solid hsl(var(--ink))", fontSize: 10,
                    cursor: !selected.size || bulkBusy ? "not-allowed" : "pointer",
                    opacity: !selected.size || bulkBusy ? 0.5 : 1,
                  }}>Approve</button>
                  <button disabled={!selected.size || bulkBusy} onClick={() => bulkSetStatus("archived")} className="caps" style={{
                    padding: "6px 10px", background: "hsl(var(--ink))", color: "hsl(var(--ink-fg))",
                    border: "1px solid hsl(var(--ink))", fontSize: 10,
                    cursor: !selected.size || bulkBusy ? "not-allowed" : "pointer",
                    opacity: !selected.size || bulkBusy ? 0.5 : 1,
                  }}>Archive</button>
                  <button disabled={!selected.size || bulkBusy} onClick={() => bulkSetStatus("spam")} className="caps" style={{
                    padding: "6px 10px", background: "hsl(var(--rust))", color: "hsl(var(--cream))",
                    border: "1px solid hsl(var(--ink))", fontSize: 10,
                    cursor: !selected.size || bulkBusy ? "not-allowed" : "pointer",
                    opacity: !selected.size || bulkBusy ? 0.5 : 1,
                  }}>Spam</button>
                </div>
              </div>
            )}
            {sugLoading && <div style={{ color: "hsl(var(--ink-2))", padding: 12 }}>Loading…</div>}
            {!sugLoading && suggestions.length === 0 && (
              <div style={{ padding: 18, fontFamily: "'Fraunces', 'Playfair Display', serif", color: "hsl(var(--ink-2))" }}>
                No suggestions yet.
              </div>
            )}
            <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
              {suggestions.map((s) => (
                <div key={s.id} style={{
                  border: "1px solid hsl(var(--ink))",
                  background: selected.has(s.id) ? "hsl(var(--paper-2))" : "hsl(var(--cream))",
                  padding: 12, display: "flex", gap: 10, alignItems: "flex-start",
                }}>
                  <input type="checkbox" checked={selected.has(s.id)} onChange={() => toggleSel(s.id)}
                    style={{ marginTop: 4, width: 18, height: 18, accentColor: "hsl(var(--ink))", cursor: "pointer" }} />
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ display: "flex", justifyContent: "space-between", alignItems: "baseline", gap: 8, flexWrap: "wrap" }}>
                      <div style={{ display: "flex", gap: 6, alignItems: "center", flexWrap: "wrap" }}>
                        <span className="caps" style={{
                          padding: "2px 8px",
                          background: s.category === "bug" ? "hsl(var(--rust))" : s.category === "idea" ? "hsl(var(--olive))" : "hsl(var(--ink))",
                          color: "hsl(var(--cream))", fontSize: 10,
                        }}>{s.category}</span>
                        <span className="caps" style={{
                          padding: "2px 8px", border: "1px solid hsl(var(--ink))",
                          color: "hsl(var(--ink))", fontSize: 10,
                        }}>{s.status}</span>
                      </div>
                      <span className="caps" style={{ color: "hsl(var(--ink-2))", fontSize: 10 }}>
                        {new Date(s.created_at).toLocaleDateString()}
                      </span>
                    </div>
                    <div style={{
                      marginTop: 8, fontFamily: "'Fraunces', 'Playfair Display', serif", fontSize: 14,
                      color: "hsl(var(--ink))", lineHeight: 1.4, whiteSpace: "pre-wrap",
                    }}>{s.body}</div>
                  </div>
                </div>
              ))}
            </div>
          </>
        )}

        {/* ─── Lessons ─── */}
        {tab === "lessons" && (
          <>
            <div style={{ display: "flex", gap: 8, marginBottom: 12, alignItems: "center" }}>
              <div style={{ flex: 1, fontFamily: "'IBM Plex Mono', monospace", fontSize: 11, color: "hsl(var(--ink-2))" }}>
                {lessons.length} active {lessons.length === 1 ? "lesson" : "lessons"} injected into Fig.
              </div>
              <button onClick={runDistill} disabled={running} className="caps" style={{
                background: "hsl(var(--ink))", color: "hsl(var(--ink-fg))",
                border: "1px solid hsl(var(--ink))", padding: "8px 12px",
                cursor: running ? "wait" : "pointer", opacity: running ? 0.6 : 1,
              }}>{running ? "Running…" : "Distill now"}</button>
            </div>
            {lessonsLoading && <div style={{ color: "hsl(var(--ink-2))", padding: 12 }}>Loading…</div>}
            {!lessonsLoading && lessons.length === 0 && (
              <div style={{ padding: 18, fontFamily: "'Fraunces', 'Playfair Display', serif", color: "hsl(var(--ink-2))" }}>
                No distilled lessons yet — feedback needs to accumulate first.
              </div>
            )}
            <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
              {lessons.map((l) => (
                <div key={l.id} style={{ border: "1px solid hsl(var(--ink))", background: "hsl(var(--cream))", padding: 12 }}>
                  <div style={{ display: "flex", justifyContent: "space-between", alignItems: "baseline", gap: 8 }}>
                    <span className="caps" style={{ color: "hsl(var(--rust))" }}>
                      {l.scope_kind} · {l.scope_label}
                    </span>
                    <span className="caps" style={{ color: "hsl(var(--ink-2))", fontSize: 10 }}>
                      {l.sample_count} notes
                    </span>
                  </div>
                  <div style={{
                    marginTop: 8, fontFamily: "'IBM Plex Mono', monospace", fontSize: 12,
                    color: "hsl(var(--ink))", lineHeight: 1.5, whiteSpace: "pre-wrap",
                  }}>{l.lessons}</div>
                  <div className="caps" style={{ marginTop: 6, color: "hsl(var(--ink-2))", fontSize: 10 }}>
                    Updated {new Date(l.updated_at).toLocaleString()}
                  </div>
                </div>
              ))}
            </div>
          </>
        )}
        {/* ─── Waitlist ─── */}
        {tab === "waitlist" && (
          <>
            <div style={{ display: "flex", gap: 6, marginBottom: 12, alignItems: "center" }}>
              <div style={{ display: "flex", gap: 4, flex: 1 }}>
                {(["pending", "all"] as const).map((f) => (
                  <button key={f} onClick={() => setWaitlistFilter(f)} style={{
                    flex: 1, padding: "8px",
                    background: waitlistFilter === f ? "hsl(var(--ink))" : "hsl(var(--cream))",
                    color: waitlistFilter === f ? "hsl(var(--ink-fg))" : "hsl(var(--ink))",
                    border: "1px solid hsl(var(--ink))",
                    fontFamily: "'IBM Plex Mono', monospace", fontSize: 11,
                    letterSpacing: "0.08em", textTransform: "uppercase", cursor: "pointer",
                  }}>{f === "pending" ? "Pending" : "All"}</button>
                ))}
              </div>
              <button onClick={loadWaitlist} disabled={waitlistLoading} className="caps" style={{
                padding: "8px 12px", background: "hsl(var(--cream))",
                border: "1px solid hsl(var(--ink))", color: "hsl(var(--ink))",
                cursor: waitlistLoading ? "wait" : "pointer", fontSize: 10,
                opacity: waitlistLoading ? 0.5 : 1,
              }}>{waitlistLoading ? "Loading…" : "Refresh"}</button>
            </div>
            {waitlistLoading && <div style={{ color: "hsl(var(--ink-2))", padding: 12 }}>Loading…</div>}
            {!waitlistLoading && waitlist.length === 0 && (
              <div style={{ padding: 18, fontFamily: "'Fraunces', 'Playfair Display', serif", color: "hsl(var(--ink-2))" }}>
                {waitlistFilter === "pending" ? "No pending signups." : "No waitlist entries yet."}
              </div>
            )}
            <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
              {waitlist.map((entry) => {
                const busy = inviting.has(entry.id);
                return (
                  <div key={entry.id} style={{
                    border: "1px solid hsl(var(--ink))",
                    background: entry.approved ? "hsl(var(--paper-3))" : "hsl(var(--cream))",
                    padding: 12, display: "flex", gap: 10, alignItems: "center",
                  }}>
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{
                        fontFamily: "'Fraunces', 'Playfair Display', serif",
                        fontWeight: 700, fontSize: 15, color: "hsl(var(--ink))",
                      }}>{entry.first_name}</div>
                      <div style={{
                        fontFamily: "'IBM Plex Mono', monospace", fontSize: 11,
                        color: "hsl(var(--ink-2))", marginTop: 2,
                      }}>{entry.email}</div>
                      <div className="caps" style={{ color: "hsl(var(--ink-2))", fontSize: 10, marginTop: 4 }}>
                        Joined {new Date(entry.created_at).toLocaleDateString()}
                        {entry.approved && entry.invited_at && ` · Invited ${new Date(entry.invited_at).toLocaleDateString()}`}
                      </div>
                    </div>
                    <div style={{ display: "flex", flexDirection: "column", gap: 6, alignItems: "flex-end" }}>
                      {entry.approved ? (
                        <>
                          <span className="caps" style={{
                            padding: "3px 8px", background: "hsl(var(--olive))", color: "hsl(var(--cream))",
                            fontSize: 9, whiteSpace: "nowrap",
                          }}>Invited</span>
                          <button
                            onClick={() => unapprove(entry)}
                            disabled={busy}
                            className="caps"
                            style={{
                              padding: "4px 8px", whiteSpace: "nowrap", fontSize: 9,
                              background: "transparent", color: "hsl(var(--rust))",
                              border: "1px dashed hsl(var(--rust))",
                              cursor: busy ? "wait" : "pointer", opacity: busy ? 0.5 : 1,
                            }}
                          >{busy ? "…" : "Unapprove"}</button>
                        </>
                      ) : (
                        <button
                          onClick={() => approveAndInvite(entry)}
                          disabled={busy}
                          className="caps"
                          style={{
                            padding: "8px 12px", whiteSpace: "nowrap",
                            background: busy ? "hsl(var(--ink))" : "hsl(var(--rust))",
                            color: "hsl(var(--cream))",
                            border: "1px solid hsl(var(--ink))",
                            cursor: busy ? "wait" : "pointer",
                            opacity: busy ? 0.7 : 1, fontSize: 10,
                          }}
                        >{busy ? "Sending…" : "Approve & Invite"}</button>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          </>
        )}

        {/* ─── Direct moderator invites ─── */}
        {tab === "invites" && (
          <>
            <p style={{ fontFamily: "'Fraunces', 'Playfair Display', serif", color: "hsl(var(--ink-2))", marginTop: 0, marginBottom: 14 }}>
              Pick a collector by handle and send them an invite to become a verified moderator.
              They'll get an in-app notification with a friendly explainer + accept/decline buttons.
            </p>

            <div style={{ display: "flex", flexDirection: "column", gap: 10, marginBottom: 14 }}>
              <div className="caps" style={{ color: "hsl(var(--ink-2))" }}>Handle</div>
              <div style={{ display: "flex", gap: 8 }}>
                <input
                  type="text"
                  placeholder="@yourname"
                  value={inviteHandleInput}
                  onChange={(e) => { setInviteHandleInput(e.target.value); setInviteCandidate(null); setInviteLookupErr(null); }}
                  style={{
                    flex: 1, padding: "10px 12px",
                    border: "1px solid hsl(var(--ink))",
                    background: "hsl(var(--paper))",
                    fontFamily: "'IBM Plex Mono', monospace", fontSize: 13,
                    color: "hsl(var(--ink))", outline: "none",
                  }}
                />
                <button
                  onClick={lookupInviteCandidate}
                  className="caps"
                  style={{
                    padding: "10px 14px",
                    background: "hsl(var(--cream))",
                    color: "hsl(var(--ink))",
                    border: "1px solid hsl(var(--ink))",
                    cursor: "pointer", fontSize: 10,
                  }}
                >
                  Look up
                </button>
              </div>
              {inviteLookupErr && (
                <div style={{ color: "hsl(var(--rust))", fontFamily: "'IBM Plex Mono', monospace", fontSize: 11 }}>
                  {inviteLookupErr}
                </div>
              )}
              {inviteCandidate && (
                <div style={{
                  padding: 10,
                  border: "1px solid hsl(var(--olive))",
                  background: "hsl(var(--cream))",
                  fontFamily: "'Fraunces', 'Playfair Display', serif",
                  color: "hsl(var(--ink))",
                }}>
                  <div style={{ fontWeight: 700, fontSize: 14 }}>
                    {inviteCandidate.display_name || `@${inviteCandidate.handle}`}
                  </div>
                  <div className="caps" style={{ color: "hsl(var(--ink-2))", fontSize: 10, marginTop: 2 }}>
                    @{inviteCandidate.handle}
                  </div>
                </div>
              )}
            </div>

            <div style={{ display: "flex", flexDirection: "column", gap: 6, marginBottom: 14 }}>
              <div className="caps" style={{ color: "hsl(var(--ink-2))" }}>Message (optional)</div>
              <textarea
                placeholder="Why are you inviting them? Shown to the user on the invite page."
                rows={3}
                value={inviteMessage}
                onChange={(e) => setInviteMessage(e.target.value)}
                style={{
                  width: "100%", padding: "10px 12px",
                  border: "1px solid hsl(var(--ink))",
                  background: "hsl(var(--paper))",
                  fontFamily: "'Fraunces', 'Playfair Display', serif", fontSize: 14,
                  color: "hsl(var(--ink))", outline: "none",
                  resize: "vertical",
                  boxSizing: "border-box",
                }}
              />
            </div>

            <button
              onClick={sendModeratorInvite}
              disabled={!inviteCandidate || inviteBusy}
              style={{
                width: "100%",
                padding: "14px 18px",
                background: !inviteCandidate || inviteBusy ? "hsl(var(--paper-3))" : "hsl(var(--ink))",
                color: !inviteCandidate || inviteBusy ? "hsl(var(--ink-2))" : "hsl(var(--ink-fg))",
                border: "1px solid hsl(var(--ink))",
                fontFamily: "'IBM Plex Mono', monospace", fontSize: 12,
                letterSpacing: "0.18em", textTransform: "uppercase",
                cursor: !inviteCandidate || inviteBusy ? "not-allowed" : "pointer",
                opacity: inviteBusy ? 0.7 : 1,
              }}
            >
              {inviteBusy ? "Sending…" : "Send invite"}
            </button>
          </>
        )}

        {/* ─── Applications ───────────────────────────────────────
            Moderator applications — review/approve/reject inbound
            requests to become a verified collector. Approve flips
            `profiles.is_verified_moderator = true`; reject prompts
            for reviewer notes. */}
        {tab === "applications" && (
          <>
            {applicationsLoading && <div style={{ color: "hsl(var(--ink-2))", padding: 12 }}>Loading…</div>}
            {!applicationsLoading && applications.length === 0 && (
              <div style={{ padding: 18, fontFamily: "'Fraunces', 'Playfair Display', serif", color: "hsl(var(--ink-2))" }}>
                No applications yet.
              </div>
            )}
            <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
              {applications.map((app) => {
                const expanded = expandedApp.has(app.id);
                const isPending = app.status === "pending";
                const created = new Date(app.created_at).toLocaleDateString();
                return (
                  <div
                    key={app.id}
                    style={{
                      border: "1px solid hsl(var(--ink))",
                      background: isPending ? "hsl(var(--cream))" : "hsl(var(--paper-3))",
                      padding: 12,
                    }}
                  >
                    <div style={{ display: "flex", justifyContent: "space-between", alignItems: "baseline", gap: 8, flexWrap: "wrap" }}>
                      <div style={{
                        fontFamily: "'Fraunces', 'Playfair Display', serif",
                        fontWeight: 700, fontSize: 15, color: "hsl(var(--ink))",
                      }}>
                        {app.handle ? `@${app.handle}` : "—"}
                      </div>
                      <div style={{ display: "flex", gap: 6, alignItems: "center", flexWrap: "wrap" }}>
                        <span className="caps" style={{
                          padding: "2px 8px",
                          background: app.status === "approved" ? "hsl(var(--olive))"
                                    : app.status === "rejected" ? "hsl(var(--rust))"
                                    : "hsl(var(--ink))",
                          color: "hsl(var(--cream))",
                          fontSize: 9, letterSpacing: "0.08em",
                        }}>{app.status}</span>
                        <span className="caps" style={{ color: "hsl(var(--ink-2))", fontSize: 10 }}>
                          {created}
                        </span>
                      </div>
                    </div>

                    <div style={{
                      marginTop: 10,
                      display: "grid", gridTemplateColumns: "auto 1fr", gap: "6px 12px",
                      fontFamily: "'IBM Plex Mono', monospace", fontSize: 11,
                      color: "hsl(var(--ink))",
                    }}>
                      <span className="caps" style={{ color: "hsl(var(--ink-2))" }}>Specialty</span>
                      <span>{app.specialty ?? "—"}</span>
                      <span className="caps" style={{ color: "hsl(var(--ink-2))" }}>Collecting</span>
                      <span>{app.years_collecting ?? "—"}</span>
                      <span className="caps" style={{ color: "hsl(var(--ink-2))" }}>Closet</span>
                      <span>{app.closet_pieces ?? "—"}</span>
                    </div>

                    {app.motivation && (
                      <div style={{ marginTop: 10 }}>
                        <button
                          onClick={() => toggleExpandedApp(app.id)}
                          className="caps"
                          style={{
                            background: "transparent", border: "none", padding: 0,
                            color: "hsl(var(--rust))", fontSize: 10, cursor: "pointer",
                          }}
                        >
                          {expanded ? "Hide motivation ↑" : "Read motivation ↓"}
                        </button>
                        {expanded && (
                          <div style={{
                            marginTop: 8, padding: 10,
                            background: "hsl(var(--paper-2))",
                            border: "1px dashed hsl(var(--sand-deep))",
                            fontFamily: "'Fraunces', 'Playfair Display', serif",
                            fontSize: 13, color: "hsl(var(--ink))",
                            whiteSpace: "pre-wrap", lineHeight: 1.4,
                          }}>{app.motivation}</div>
                        )}
                      </div>
                    )}

                    {app.reviewer_notes && (
                      <div className="caps" style={{
                        marginTop: 10, padding: 8,
                        background: "hsl(var(--paper-2))", border: "1px solid hsl(var(--sand-deep))",
                        fontSize: 10, color: "hsl(var(--ink-2))",
                        textTransform: "none", letterSpacing: 0,
                        fontFamily: "'IBM Plex Mono', monospace",
                      }}>
                        Reviewer notes: {app.reviewer_notes}
                      </div>
                    )}

                    {isPending && (
                      <div style={{ display: "flex", gap: 6, marginTop: 12, flexWrap: "wrap" }}>
                        <button
                          onClick={() => approveApplication(app)}
                          className="caps"
                          style={{
                            flex: 1, padding: "8px",
                            background: "hsl(var(--olive))", color: "hsl(var(--cream))",
                            border: "1px solid hsl(var(--ink))", cursor: "pointer", fontSize: 10,
                          }}
                        >Approve</button>
                        <button
                          onClick={() => rejectApplication(app)}
                          className="caps"
                          style={{
                            flex: 1, padding: "8px",
                            background: "hsl(var(--rust))", color: "hsl(var(--cream))",
                            border: "1px solid hsl(var(--ink))", cursor: "pointer", fontSize: 10,
                          }}
                        >Reject</button>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          </>
        )}

        {/* ─── Suspensions ────────────────────────────────────────
            Auto-suspended scans (fast-fashion reports or rule hits).
            Confirm to send a "post_removed" notification to the
            poster; Restore to clear the suspension flag and notify. */}
        {tab === "suspensions" && (
          <>
            {suspendedLoading && <div style={{ color: "hsl(var(--ink-2))", padding: 12 }}>Loading…</div>}
            {!suspendedLoading && suspended.length === 0 && (
              <div style={{ padding: 18, fontFamily: "'Fraunces', 'Playfair Display', serif", color: "hsl(var(--ink-2))" }}>
                Nothing suspended.
              </div>
            )}
            <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
              {suspended.map((s) => {
                const reportN = reportCounts[s.id] ?? 0;
                const when = s.suspended_at
                  ? new Date(s.suspended_at).toLocaleString()
                  : "—";
                return (
                  <div
                    key={s.id}
                    style={{
                      border: "1px solid hsl(var(--ink))",
                      background: "hsl(var(--cream))",
                      padding: 12,
                    }}
                  >
                    <div style={{ display: "flex", justifyContent: "space-between", alignItems: "baseline", gap: 8, flexWrap: "wrap" }}>
                      <div style={{
                        fontFamily: "'Fraunces', 'Playfair Display', serif",
                        fontWeight: 700, fontSize: 15, color: "hsl(var(--ink))",
                      }}>
                        {s.title ?? "Untitled scan"}
                      </div>
                      {reportN > 0 && (
                        <span className="caps" style={{
                          padding: "2px 8px", background: "hsl(var(--rust))", color: "hsl(var(--cream))",
                          fontSize: 9, letterSpacing: "0.08em",
                        }}>{reportN} report{reportN === 1 ? "" : "s"}</span>
                      )}
                    </div>

                    <div className="caps" style={{ marginTop: 4, color: "hsl(var(--ink-2))", fontSize: 10 }}>
                      {s.brand ?? "Unknown"} · {s.category ?? "—"} · {s.era ?? "—"}
                    </div>

                    {s.suspension_reason && (
                      <div style={{
                        marginTop: 8, padding: 8,
                        background: "hsl(var(--paper-2))",
                        border: "1px dashed hsl(var(--sand-deep))",
                        fontFamily: "'IBM Plex Mono', monospace", fontSize: 11,
                        color: "hsl(var(--ink))", whiteSpace: "pre-wrap",
                      }}>{s.suspension_reason}</div>
                    )}

                    <div className="caps" style={{ marginTop: 8, color: "hsl(var(--ink-2))", fontSize: 10 }}>
                      Suspended {when}{s.posted === false && " · already unposted"}
                    </div>

                    <div style={{ display: "flex", gap: 6, marginTop: 10, flexWrap: "wrap" }}>
                      <button
                        onClick={() => confirmSuspension(s)}
                        className="caps"
                        style={{
                          flex: 1, padding: "8px",
                          background: "hsl(var(--ink))", color: "hsl(var(--ink-fg))",
                          border: "1px solid hsl(var(--ink))", cursor: "pointer", fontSize: 10,
                        }}
                      >Confirm removal</button>
                      <button
                        onClick={() => restorePost(s)}
                        className="caps"
                        style={{
                          flex: 1, padding: "8px",
                          background: "hsl(var(--olive))", color: "hsl(var(--cream))",
                          border: "1px solid hsl(var(--ink))", cursor: "pointer", fontSize: 10,
                        }}
                      >Restore post</button>
                    </div>
                  </div>
                );
              })}
            </div>
          </>
        )}

        {/* ─── Tele (pricing telemetry) ─── */}
        {tab === "tele" && <PricingTelemetryPanel embedded />}

      </div>
    </MobileFrame>
  );
}
